import { grey } from "@mui/material/colors";

import { format } from "date-fns";
import { max } from "lodash";

import type { SelectSaleConfigurationValues } from "components/word-processor-field/dialogs/word-processor-select-sale-configuration-dialog.component";
import { PRODUCT_SUB_TYPES } from "constants/products";
import {
  FEE_PRODUCT_TYPE,
  PRODUCT_TYPES,
  SERVICE_PRODUCT_TYPE
} from "constants/sales";
import { getEarText } from "features/inventory/orders/orders.util";
import type { Product } from "services/types/products.type";
import { type LineItem, type Sale } from "services/types/sales.type";
import {
  formatDateOnlyValue,
  formatMoney,
  formatMoneyValue,
  formatPercent
} from "utils/common";
import {
  getLineItemEarDisplay,
  getLineItemSerialRaw
} from "utils/line-item-display.util";

import {
  getConfigurableLineItemValue,
  getConfigurableLineItems,
  getConfigurableLineItemsHeaders,
  getConfigurableTotals
} from "./configurable-merge-tags";
import { getSaleTotals, getSortedItems } from "./utils";

export const getSalesItemsTableHtml = (sale: Sale) => {
  let result = `<table style="border-collapse: collapse; width: 100%;" border="1">
  <colgroup>
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
    <col style="width: 11.1111%;">
  </colgroup>
  <tbody>
    <tr>
      <td><strong>Description</strong></td>
      <td><strong>Type</strong></td>
      <td><strong>Unit Price</strong></td>
      <td><strong>Tax</strong></td>
      <td><strong>Discount</strong></td>
      <td><strong>Quantity</strong></td>
      <td><strong>Line Total</strong></td>
      <td><strong>Patient Responsibility</strong></td>
      <td><strong>Insurance Responsibility</strong></td>
    </tr>`;

  sale?.items?.forEach((saleLineItem) => {
    const serialNumbers = getLineItemSerialRaw(saleLineItem);
    const ears = getLineItemEarDisplay(saleLineItem);

    let row = `<td>${saleLineItem.description ?? ""}${
      serialNumbers ? " - S/N: " + serialNumbers : ""
    }}${ears ? " - Ear: " + ears : ""} ${
      saleLineItem.returned_at
        ? "<br>" +
          format(new Date(String(saleLineItem.returned_at)), "MM/dd/yyyy")
        : ""
    }
      </td>`;

    row =
      row +
      `<td>${PRODUCT_TYPES.getFromCode(
        saleLineItem.product.master_product.type
      )}</td>`;

    row =
      row +
      `<td style="text-align: right">${formatMoney(
        Number(saleLineItem.price),
        2
      )}</td>`;
    row =
      row +
      `<td style="text-align: right">${
        saleLineItem.taxable && saleLineItem.tax_rate
          ? formatPercent(Number(saleLineItem.tax_rate), 0, 3)
          : "--"
      }</td>`;

    row =
      row +
      `<td style="text-align: right">${formatMoney(
        Number(saleLineItem.discount),
        2
      )}<br><span style="color:${grey[600]}">${
        saleLineItem?.discount && Number(saleLineItem.discount) > 0
          ? String(
              Math.round(
                (Number(saleLineItem.discount) / Number(saleLineItem.price)) *
                  100 *
                  100
              ) / 100
            )
          : "0"
      }%</span></td>`;

    row = row + `<td style="text-align: right">${saleLineItem.quantity}</td>`;
    row =
      row +
      `<td style="text-align: right">${formatMoney(
        Number(saleLineItem.total),
        2
      )}</td>`;

    row =
      row +
      `<td style="text-align: right">${formatMoney(
        Number(saleLineItem.patient_responsibility),
        2
      )}</td>`;

    row =
      row +
      `<td style="text-align: right">${formatMoney(
        Number(saleLineItem.insurer_responsibility),
        2
      )}</td>`;

    result = result + "<tr>" + row + "</tr>";
  });

  result =
    result +
    `<tr style="border: none">
      <td colspan="9"><br></td>
    </tr>`;

  result =
    result +
    `<tr style="border: none">
      <td colspan="7"><br></td>
      <td>Patient Responsibility</td>
      <td style="text-align: right">${formatMoney(
        Number(sale.patient_responsibility_total)
      )}</td>
    </tr>`;

  result =
    result +
    `<tr style="border: none">
      <td colspan="7"><br></td>
      <td>Insurance Responsibility</td>
      <td style="text-align: right">${formatMoney(
        Number(sale.insurer_responsibility_total)
      )}</td>
    </tr>`;

  result =
    result +
    `<tr style="border: none">
      <td colspan="7"><br></td>
      <td style="font-weight: 700">Sale Total</td>
      <td style="font-weight: 700;text-align: right">${formatMoney(
        Number(sale.total)
      )}</td>
    </tr>`;

  result = result + `</tbody></table>`;
  return result;
};

export const getLineItemsTableHtml = (items: LineItem[] | undefined) => {
  let result = `<table style="border-collapse: collapse; width: 100%;" border="1">
  <colgroup>
    <col style="width: 14.2857%;">
    <col style="width: 14.2857%;">
    <col style="width: 14.2857%;">
    <col style="width: 14.2857%;">
    <col style="width: 14.2857%;">
    <col style="width: 14.2857%;">
    <col style="width: 14.2857%;">
  </colgroup>
  <tbody>
    <tr>
      <td style="text-align: center"><strong>Ear</strong></td>
      <td style="text-align: center"><strong>Manufacturer</strong></td>
      <td style="text-align: center"><strong>Model</strong></td>
      <td style="text-align: center"><strong>Serial #</strong></td>
      <td style="text-align: center"><strong>MFR Warranty Exp</strong></td>
      <td style="text-align: center"><strong>L&D Exp</strong></td>
      <td style="text-align: center; width: 120px"><strong>Price</strong></td>
    </tr>`;

  items?.forEach((item) => {
    const ear = getEarText(item.inventory_product?.ear);
    const manufacturer = item.product?.master_product?.manufacturer?.name ?? "";
    const model = item.product?.master_product?.model ?? "";
    const serialNumber = item.inventory_product?.serial_number ?? "";

    const manufacturer_warranty_expiration_date =
      item.inventory_product?.manufacturer_warranty_expiration_date ?? "";

    const loss_damage_warranty_expiration_date =
      item.inventory_product?.loss_damage_warranty_expiration_date ?? "";

    const price = formatMoneyValue(Number(item.price || "0"));

    result = `${result}
    <tr>
      <td>${ear}</td>
      <td>${manufacturer}</td>
      <td>${model}</td>
      <td>${serialNumber}</td>
      <td style="text-align: right">${
        manufacturer_warranty_expiration_date
          ? format(
              new Date(manufacturer_warranty_expiration_date),
              "MM/dd/yyyy"
            )
          : ""
      }</td>
      <td style="text-align: right">${
        loss_damage_warranty_expiration_date
          ? format(new Date(loss_damage_warranty_expiration_date), "MM/dd/yyyy")
          : ""
      }</td>
      <td style="text-align: right; width: 120px">${price}</td>
    </tr>`;
  });

  return `${result}</tbody></table>`;
};

const getProductTypeText = (product: Product) => {
  const type = product?.master_product?.type || product.type;
  const subtype = product?.master_product?.subtype;

  if (!type) return "";
  if (type && subtype) {
    return `${PRODUCT_TYPES.getFromCode(
      type
    )} (${PRODUCT_SUB_TYPES.getFromCode(subtype)})`;
  }

  return PRODUCT_TYPES.getFromCode(type);
};

export const getSaleDeviceLineItems = (items: LineItem[]) => {
  return items.filter((item) => {
    const type = item.product?.master_product?.type || item.product?.type;
    return type !== SERVICE_PRODUCT_TYPE && type !== FEE_PRODUCT_TYPE;
  });
};

export const getSaleServiceLineItems = (items: LineItem[]) => {
  return items.filter((item) => {
    const type = item.product?.master_product?.type || item.product?.type;
    return type === SERVICE_PRODUCT_TYPE || type === FEE_PRODUCT_TYPE;
  });
};

const getTotalsHtmlRow = (sale: Sale, showResponsibility?: boolean) => {
  const {
    paymentMethods,
    totalDiscounts,
    totalTax,
    totalServices,
    total,
    balance
  } = getSaleTotals(sale);

  return `
  <tr>
    <td colspan="${showResponsibility ? 10 : 9}" style="text-align: right;">Services/Dispensing&nbsp;Fees</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalServices)}</td>
  </tr>
  <tr>
    <td colspan="${showResponsibility ? 10 : 9}" style="text-align: right;">Total Discounts</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalDiscounts)}</td>
  </tr>
  <tr>
    <td colspan="${showResponsibility ? 10 : 9}" style="text-align: right">Total Tax</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalTax)}</td>
  </tr>
  <tr>
    <td colspan="${showResponsibility ? 10 : 9}" style="text-align: right"><strong>Total Amount</strong></td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(total)}</td>
  </tr>
  <tr>
    <td colspan="${showResponsibility ? 10 : 9}" style="text-align: left">Payment Method${paymentMethods?.length > 1 ? "s:" : ":"} ${paymentMethods.join(", ")}</td>
    <td style="text-align: right; width: 120px"></td>
  </tr>
  <tr>
    <td colspan="${showResponsibility ? 10 : 9}" style="text-align: right"><strong>Balance Due</strong></td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(balance, balance < 0)}</td>
  </tr>
  `;
};

const SALE_LINE_ITEMS_TABLE_COLGROUP = `<colgroup>
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
    <col style="width: 10%;">
  </colgroup>`;

export const getSaleLineItemsTotalsTableHtml = (sale: Sale | undefined) => {
  if (!sale) return undefined;

  const items = sale?.items || [];
  if (!items.length) return "";

  let result = `<table style="border-collapse: collapse; width: 100%;" border="1">
   ${SALE_LINE_ITEMS_TABLE_COLGROUP}
  <tbody>
    `;

  return `${result}${getTotalsHtmlRow(sale)}</tbody></table>`;
};

export const getSaleLineItemsServicesTableHtml = (
  sale: Sale | undefined,
  hidePrice?: boolean
) => {
  if (!sale) return undefined;
  const filteredItems = getSortedItems(
    getSaleServiceLineItems(sale?.items || [])
  );
  if (!filteredItems.length) return "";

  const hasDiscount = hidePrice
    ? false
    : filteredItems.some((item) => Number(item.discount) > 0);

  let result = `<table style="border-collapse: collapse; width: 100%;" border="1">
   ${SALE_LINE_ITEMS_TABLE_COLGROUP}
  <tbody>
    <tr>
      <td style="text-align: center"><strong>CPT Code</strong></td>
      <td colspan="${hidePrice ? 3 : 2}" style="text-align: center"><strong>Product</strong></td>
      <td colspan="${hasDiscount ? 4 : 5}" style="text-align: center"><strong>Description</strong></td>
      ${hasDiscount ? '<td style="text-align: center"><strong>Discount</strong></td>' : ""}
      ${hidePrice ? "" : '<td style="text-align: center; width: 120px"><strong>Price</strong></td>'}
      <td style="text-align: center"><strong>Quantity</strong></td>
    </tr>
      `;

  filteredItems.forEach((item) => {
    const cptCode = item.cpt_code?.code ?? "";
    const product = getProductTypeText(item.product);

    const quantity = Number(item.quantity || 1);
    const price = formatMoneyValue(Number(item.price || "0") * quantity);

    const description = item.description;
    const discount = Number(item.discount)
      ? formatMoneyValue(Number(item.discount))
      : "";

    result = `${result}
    <tr>
      <td>${cptCode}</td>
      <td colspan="${hidePrice ? 3 : 2}">${product}</td>
      <td colspan="${hasDiscount ? 4 : 5}">${description}</td>
      ${hasDiscount ? '<td style="text-align: right">' + discount + "</td>" : ""}
      ${hidePrice ? "" : '<td style="text-align: right; width: 120px">' + price + "</td>"}
      <td style="text-align: right">${quantity}</td>
      </tr>
      `;
  });

  return `${result}</tbody></table>`;
};

export const getTotalsWithPredefinedParametersHtmlRow = (params: {
  sale: Sale;
  total_discounts: boolean;
  total_tax: boolean;
  total_amount: boolean;
  total_patient_payments: boolean;
  total_insurance_payments: boolean;
  amount_due_from_insurance: boolean;
  amount_due_from_patient: boolean;
  balance_due: boolean;
  show_responsibility: boolean;
  services_dispensing_fees: boolean;
}) => {
  const {
    sale,
    total_discounts,
    total_tax,
    total_amount,
    total_patient_payments,
    total_insurance_payments,
    amount_due_from_insurance,
    amount_due_from_patient,
    balance_due,
    show_responsibility,
    services_dispensing_fees
  } = params;
  const {
    paymentMethods,
    totalDiscounts,
    totalTax,
    totalServices,
    total,
    balance,
    totalInsurancePayments,
    totalPatientPayments,
    amountDueFromInsurance,
    amountDueFromPatient
  } = getSaleTotals(sale);

  let result = "";

  let colspan = show_responsibility ? 10 : 9;

  if (services_dispensing_fees) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right;">Services/Dispensing&nbsp;Fees</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalServices)}</td>
  </tr>`;
  }
  if (total_discounts) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right;">Total Discounts</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalDiscounts)}</td>
  </tr>`;
  }

  if (total_tax) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right">Total Tax</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalTax)}</td>
  </tr>`;
  }

  if (total_amount) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right"><strong>Total Amount</strong></td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(total)}</td>
  </tr>`;
  }

  if (total_patient_payments) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right">Total Patient Payments</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalPatientPayments)}</td>
  </tr>`;

    if (paymentMethods.length) {
      result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: left">Payment Method${paymentMethods?.length > 1 ? "s:" : ":"} ${paymentMethods.join(", ")}</td>
    <td style="text-align: right; width: 120px"></td>
  </tr>`;
    }
  }

  if (total_insurance_payments) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right">Total Insurance Payments</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(totalInsurancePayments)}</td>
  </tr>`;
  }

  if (amount_due_from_insurance) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right">Amount Due From Insurance</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(amountDueFromInsurance)}</td>
  </tr>`;
  }

  if (amount_due_from_patient) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right">Amount Due From Patient</td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(amountDueFromPatient)}</td>
  </tr>`;
  }

  if (balance_due) {
    result = `${result}<tr>
    <td colspan="${colspan}" style="text-align: right"><strong>Balance Due</strong></td>
    <td style="text-align: right; width: 120px">${formatMoneyValue(balance, balance < 0)}</td>
  </tr>`;
  }

  return result;
};

const MAX_TABLE_WIDTH = 1270;
const EXTENDED_COLUMN_WIDTH = 140;

const getColumnWidths = (headers: string[]) => {
  const headerCount = headers.length;
  const hasManufacturer = headers.includes("Manufacturer");
  const regularColumnsCount = hasManufacturer
    ? max([headerCount - 1, 1]) || 1
    : headerCount;

  const availableWidth =
    MAX_TABLE_WIDTH - EXTENDED_COLUMN_WIDTH - regularColumnsCount;
  const regularColumnWidth = availableWidth / regularColumnsCount;

  return { regularColumnWidth, extendedColumnWidth: EXTENDED_COLUMN_WIDTH };
};

const getFontSize = (length: number) => {
  let fontSize = "12px";

  if (length >= 20) {
    fontSize = "9px";
  } else if (length >= 19) {
    fontSize = "10px";
  } else if (length >= 16) {
    fontSize = "11px";
  }

  return fontSize;
};

const buildTableHeader = (
  headers: string[],
  regularColumnWidth: number,
  extendedColumnWidth: number
) => {
  let headerHtml = `<table style="border-collapse: collapse; width: 100%;" border="1"><thead><tr>`;

  const fontSize = getFontSize(headers.length);

  headers.forEach((header) => {
    const isExtended = header === "Manufacturer";
    const columnWidth = isExtended ? extendedColumnWidth : regularColumnWidth;
    const extendedStyle = isExtended ? `width:${EXTENDED_COLUMN_WIDTH}px` : "";
    const displayText = header === "Serial #" ? "Serial&nbsp;#" : header;

    headerHtml += `<td style="font-size:${fontSize};box-sizing:border-box;text-align:center;flex:${columnWidth};${extendedStyle}"><span style="font-size:${fontSize};"><strong>${displayText}</strong></span></td>`;
  });

  headerHtml += `</tr></thead><tbody>`;
  return headerHtml;
};

const buildTableCell = (
  header: string,
  value: string,
  regularColumnWidth: number,
  extendedColumnWidth: number,
  fontSize: string
) => {
  const isRightAligned =
    header === "Price" || header === "Qty" || header === "Resp.";
  const isExtended = header === "Manufacturer";
  const columnWidth = isExtended ? extendedColumnWidth : regularColumnWidth;

  const alignment = isRightAligned ? "right" : "left";

  return `<td style="font-size:${fontSize};box-sizing:border-box;text-align:${alignment};flex:${columnWidth}"><span style="font-size:${fontSize};">${value}</span></td>`;
};

const buildTableRows = (
  items: LineItem[],
  headers: string[],
  saleConfiguration: Partial<SelectSaleConfigurationValues>,
  regularColumnWidth: number,
  extendedColumnWidth: number
) => {
  let rowsHtml = "";

  const fontSize = getFontSize(headers.length);

  items.forEach((item) => {
    let row = "";

    headers.forEach((header) => {
      const value = getConfigurableLineItemValue(
        item,
        header,
        saleConfiguration
      );

      row += buildTableCell(
        header,
        value,
        regularColumnWidth,
        extendedColumnWidth,
        fontSize
      );
    });

    if (row) {
      rowsHtml += `<tr>${row}</tr>`;
    }
  });

  return rowsHtml;
};

export const getSaleLineItemsTableHtmlWithPredefinedParameters = (
  sale: Sale | undefined,
  saleConfiguration: Partial<SelectSaleConfigurationValues>
) => {
  if (!sale || !saleConfiguration) return undefined;

  const items = getConfigurableLineItems(sale, saleConfiguration);
  if (!items.length && !saleConfiguration.include_totals) return "";

  const headers = getConfigurableLineItemsHeaders(saleConfiguration);
  if (!headers.length && !saleConfiguration.include_totals) return "";

  const { regularColumnWidth, extendedColumnWidth } = getColumnWidths(headers);

  let result = buildTableHeader(
    headers,
    regularColumnWidth,
    extendedColumnWidth
  );
  result += buildTableRows(
    items,
    headers,
    saleConfiguration,
    regularColumnWidth,
    extendedColumnWidth
  );

  if (saleConfiguration.include_totals) {
    if (headers.length) {
      result += `<tr><td colspan="${headers.length}">&nbsp;</td></tr>`;
    }
    result += getConfigurableTotals(sale, saleConfiguration, headers.length);
  }

  result += `</tbody></table>`;

  return result;
};

export const getSaleLineItemsTableHtml = (
  sale: Sale | undefined,
  filterFn: (items: LineItem[]) => LineItem[],
  includeTotals?: boolean,
  hidePrice?: boolean,
  showResponsibility?: boolean
) => {
  if (!sale) return undefined;
  const items = getSortedItems(filterFn(sale?.items || []));
  if (!items.length) return "";

  let result = `<table style="border-collapse: collapse; width: 100%;" border="1">
  ${SALE_LINE_ITEMS_TABLE_COLGROUP}
  <tbody>
    <tr>
      <td style="text-align: center"><strong>Ear</strong></td>
      <td ${hidePrice ? "colspan='2'" : ""} style="text-align: center"><strong>Product</strong></td>
      <td style="text-align: center;width: 120px"><strong>Manufacturer</strong></td>
      <td style="text-align: center"><strong>Model</strong></td>
      <td style="text-align: center"><strong>Color</strong></td>
      <td style="text-align: center"><strong>Serial&nbsp;#</strong></td>
      <td style="text-align: center"><strong>MFR Warranty Exp</strong></td>
      <td style="text-align: center${hidePrice ? ";width: 120px" : ""}"><strong>L&D Warranty Exp</strong></td>
      ${hidePrice ? "" : '<td style="text-align: center; width: 120px"><strong>Price</strong></td>'}
      <td style="text-align: center"><strong>Quantity</strong></td>
      ${!hidePrice && showResponsibility ? "<td style='text-align: center; width: 120px'><strong>Resp.</strong></td>" : ""}
    </tr>
      `;

  items.forEach((item) => {
    const inventoryProduct = item.inventory_product;
    const masterProduct = item.product?.master_product;

    const ear = getLineItemEarDisplay(item);
    const color = inventoryProduct?.color ?? "";

    const product = getProductTypeText(item.product);

    const manufacturer = masterProduct?.manufacturer?.name ?? "";

    const model = masterProduct?.model ?? "";
    const serialNumber = getLineItemSerialRaw(item);

    const manufacturerWarrantyExpirationDate =
      inventoryProduct?.manufacturer_warranty_expiration_date ?? "";

    const lossDamageWarrantyExpirationDate =
      inventoryProduct?.loss_damage_warranty_expiration_date ?? "";

    const quantity = Number(item.quantity || 1);
    const price = formatMoneyValue(Number(item.price || "0") * quantity);

    const resp =
      showResponsibility && !hidePrice
        ? `PT: ${formatMoneyValue(Number(item.patient_responsibility || "0"))}<br>${item.managed_care_plan?.id ? "MCP" : "INS"}: ${formatMoneyValue(Number(item.insurer_responsibility || "0"))}`
        : "";

    result = `${result}
    <tr>
      <td>${ear}</td>
      <td ${hidePrice ? "colspan='2'" : ""}>${product}</td>
      <td>${manufacturer}</td>
      <td>${model}</td>
      <td>${color}</td>
      <td>${serialNumber}</td>
      <td style="text-align: right">${
        manufacturerWarrantyExpirationDate
          ? formatDateOnlyValue(
              manufacturerWarrantyExpirationDate,
              "MMM dd, yyyy"
            )
          : ""
      }</td>
      <td style="text-align: right${hidePrice ? ";width: 120px" : ""}">${
        lossDamageWarrantyExpirationDate
          ? formatDateOnlyValue(
              lossDamageWarrantyExpirationDate,
              "MMM dd, yyyy"
            )
          : ""
      }</td>
      ${hidePrice ? "" : '<td style="text-align: right; width: 120px">' + price + "</td>"}
      <td style="text-align: right">${quantity}</td>
      ${!hidePrice && showResponsibility ? "<td style='text-align: right;width: 120px'>" + resp + "</td>" : ""}
      </tr>
      `;
  });

  if (includeTotals) {
    const colspan = hidePrice ? 9 : showResponsibility ? 11 : 10;

    result = `${result}<tr><td colspan="${colspan}">&nbsp;</td></tr>${getTotalsHtmlRow(sale, showResponsibility)}`;
  }

  return `${result}</tbody></table>`;
};
